import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  X,
  Loader2,
  Save,
  User,
  Tag,
  Building2,
  Globe,
  Pill,
  Stethoscope,
  FlaskConical,
  MessageSquare,
  StickyNote,
  BookOpen,
  CheckCircle
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

interface Props {
  open: boolean;
  type: string;
  onClose: () => void;
  onSave: (data: {
    title: string;
    description?: string;
    type: string;
    category: 'personal' | 'clinic' | 'shared';
    tags: string[];
    doctor_id: string;
    clinic_id: string;
  }) => Promise<void>;
  initialData?: {
    title?: string;
    description?: string;
    category?: 'personal' | 'clinic' | 'shared';
    tags?: string[];
  };
}

const templateTypes = {
  complaint:    { label: 'Complaints',    icon: MessageSquare,  color: 'text-medical-complaints',    bg: 'bg-medical-complaints/10' },
  medicine:     { label: 'Medicines',     icon: Pill,           color: 'text-medical-medicines',     bg: 'bg-medical-medicines/10' },
  investigation:{ label: 'Investigations',icon: FlaskConical,   color: 'text-medical-investigations',bg: 'bg-medical-investigations/10' },
  advice:       { label: 'Advice',        icon: BookOpen,       color: 'text-medical-advice',        bg: 'bg-medical-advice/10' },
  diagnosis:    { label: 'Diagnosis',     icon: Stethoscope,    color: 'text-medical-diagnosis',     bg: 'bg-medical-diagnosis/10' },
  quick_notes:  { label: 'Quick Notes',   icon: StickyNote,     color: 'text-medical-notes',         bg: 'bg-medical-notes/10' }
};

const categoryOptions = [
  { value: 'personal', label: 'Personal',  icon: User,      desc: 'Only visible to you' },
  { value: 'clinic',   label: 'Clinic',    icon: Building2, desc: 'Shared with clinic staff' },
  { value: 'shared',   label: 'Shared',    icon: Globe,     desc: 'Available to all users' }
] as const;

export default function SaveTemplateModal({ open, type, onClose, onSave, initialData }: Props) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'personal' | 'clinic' | 'shared'>('personal');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();

  useEffect(() => {
    if (open) {
      setTitle(initialData?.title || '');
      setDescription(initialData?.description || '');
      setCategory(initialData?.category || 'personal');
      setTags(initialData?.tags || []);
      setTagInput('');
      setSaving(false);
      setSaved(false);
      setError('');
    }
  }, [open, initialData]);

  const addTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed) && tags.length < 10) {
      setTags([...tags, trimmed]);
      setTagInput('');
    }
  };

  const removeTag = (idx: number) => setTags(tags.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!title.trim()) { setError("Template name is required"); return; }
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) {
      setError("User information missing. Please log in again.");
      return;
    }

    // Flush any pending tag input
    const finalTags = tagInput.trim() && !tags.includes(tagInput.trim())
      ? [...tags, tagInput.trim()]
      : tags;

    try {
      setSaving(true);
      setError('');
      await onSave({
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        category,
        tags: finalTags,
        doctor_id: currentUser.doctor_id,
        clinic_id: currentUser.clinic_id
      });
      setSaved(true);
      setTimeout(() => onClose(), 1000);
    } catch (err: any) {
      setError(err?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const typeConfig = templateTypes[type as keyof typeof templateTypes] || templateTypes.medicine;
  const TypeIcon = typeConfig.icon;

  return (
    <Dialog open={open} onOpenChange={() => !saving && onClose()}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden gap-0">

        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", typeConfig.bg)}>
              <TypeIcon className={cn("w-5 h-5", typeConfig.color)} />
            </div>
            <div>
              <DialogTitle className="text-base font-semibold">
                Save {typeConfig.label} Template
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Save this template for quick reuse in future prescriptions
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <form
          onSubmit={(e) => { e.preventDefault(); handleSave(); }}
          className="px-6 py-5 space-y-5"
        >
          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-md px-3 py-2">
              <X className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Template Name */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              Template Name
              <span className="text-destructive">*</span>
            </Label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => { setTitle(e.target.value); setError(''); }}
              placeholder={`e.g. ${typeConfig.label} for Diabetes`}
              disabled={saving || saved}
              className={cn(error && !title.trim() && "border-destructive")}
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5 text-muted-foreground" />
              Description
              <span className="text-xs text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="When to use this template..."
              disabled={saving || saved}
              className="resize-none min-h-[70px] text-sm"
            />
          </div>

          {/* Visibility */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Visibility</Label>
            <div className="grid grid-cols-3 gap-2">
              {categoryOptions.map(({ value, label, icon: Icon, desc }) => (
                <button
                  key={value}
                  type="button"
                  disabled={saving || saved}
                  onClick={() => setCategory(value)}
                  className={cn(
                    "flex flex-col items-center gap-1 p-3 rounded-lg border text-center transition-all",
                    category === value
                      ? "border-primary bg-primary/5 text-primary"
                      : "border-border text-muted-foreground hover:border-ring/60 hover:text-foreground"
                  )}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-medium">{label}</span>
                  <span className="text-[10px] leading-tight opacity-70">{desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5 text-muted-foreground" />
              Tags
              <span className="text-xs text-muted-foreground font-normal">(optional)</span>
            </Label>
            <div className={cn(
              "flex flex-wrap items-center gap-1.5 min-h-[38px] px-3 py-2 rounded-md border border-input bg-background",
              "focus-within:ring-1 focus-within:ring-ring focus-within:border-ring"
            )}>
              {tags.map((tag, idx) => (
                <Badge key={idx} variant="secondary" className="text-xs gap-1 pr-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(idx)}
                    className="hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              <input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                    e.preventDefault();
                    addTag();
                  }
                  if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
                    removeTag(tags.length - 1);
                  }
                }}
                onBlur={addTag}
                placeholder={tags.length === 0 ? "Type and press Enter or comma..." : ""}
                disabled={saving || saved || tags.length >= 10}
                className="flex-1 min-w-[120px] text-sm outline-none bg-transparent placeholder:text-muted-foreground"
              />
            </div>
            {tags.length > 0 && (
              <p className="text-[11px] text-muted-foreground">{tags.length}/10 tags</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || saved || !title.trim()}
              size="sm"
              className="min-w-[120px]"
            >
              {saved ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Saved!
                </>
              ) : saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
